import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const GoogleButton = ({ label }) => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const go = () => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" data-testid="google-register-btn" onClick={go} variant="outline"
      className="w-full rounded-full h-11 border-brand-navy/20 text-brand-navy gap-2">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      {label}
    </Button>
  );
};

const Register = () => {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { setError(t("auth.termsRequired")); return; }
    setError(""); setLoading(true);
    try {
      await register(form.name, form.email, form.password, marketing);
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-brand-light">
      <div className="w-full max-w-md rounded-2xl border border-brand-navy/10 bg-white p-8 shadow-[0_8px_40px_rgba(8,16,33,0.08)]">
        <img src="/logo-lsh-transparent.png" alt="LearnSphereHub" className="h-12 w-12 object-contain mx-auto" />
        <h1 className="mt-5 text-2xl font-bold font-heading text-brand-navy text-center">{t("auth.registerTitle")}</h1>
        {error && <div data-testid="register-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm p-3">{error}</div>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">{t("auth.name")}</Label>
            <Input id="name" data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" data-testid="register-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" type="password" data-testid="register-password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="register-terms-wrap">
            <Checkbox data-testid="register-accept-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed text-brand-navy/60">
              {t("auth.acceptTermsA")}{" "}
              <Link to="/termini" target="_blank" className="text-brand-electric hover:underline font-medium">{t("legal.terms")}</Link>{" "}
              {t("auth.acceptTermsB")}
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="register-marketing-wrap">
            <Checkbox data-testid="register-marketing" checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed text-brand-navy/60">
              Desidero ricevere promemoria sul carrello, novità e offerte da LearnSphereHub.
            </span>
          </label>
          <Button type="submit" data-testid="register-submit" disabled={loading || !acceptTerms} className="w-full rounded-full h-11 bg-brand-electric hover:bg-brand-electric/90 text-white">
            {loading ? t("common.loading") : t("auth.registerBtn")}
          </Button>
          <p className="text-xs text-brand-navy/50 leading-relaxed" data-testid="register-privacy-note">
            {t("auth.privacyNoticeA")}{" "}
            <Link to="/privacy" target="_blank" className="text-brand-electric hover:underline font-medium">{t("legal.privacy")}</Link>
            {t("auth.privacyNoticeB")}
          </p>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-brand-navy/40"><div className="flex-1 h-px bg-brand-navy/10" />{t("auth.or")}<div className="flex-1 h-px bg-brand-navy/10" /></div>
        <GoogleButton label={t("auth.google")} />
        <p className="mt-6 text-center text-sm text-brand-navy/60">
          {t("auth.hasAccount")} <Link to="/login" data-testid="go-login" className="text-brand-electric font-semibold">{t("auth.goLogin")}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
